// Synthesized ambient bed: a low orbital pad + solar wind in space, crossfading
// into aerodynamic wind through the cloud descent. No audio files required.
export class AmbientAudio {
  constructor() {
    this.ctx = null;
    this.on = false;
    this.phase = 0;
  }

  ensure() {
    if (this.ctx) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    this.ctx = ctx;

    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    this.master = master;

    // ---- space bed: detuned low pad + deep filtered noise
    const spaceBus = ctx.createGain();
    spaceBus.gain.value = 1;
    const padFilter = ctx.createBiquadFilter();
    padFilter.type = "lowpass";
    padFilter.frequency.value = 320;
    spaceBus.connect(padFilter).connect(master);
    this.spaceBus = spaceBus;

    [[54, 0.5], [81.6, 0.3], [108.9, 0.12], [162.2, 0.07]].forEach(([f, g], i) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = f;
      osc.detune.value = i % 2 ? 4 : -3;
      const og = ctx.createGain();
      og.gain.value = g;
      osc.connect(og).connect(spaceBus);
      osc.start();
    });

    const noiseBuf = this.makeNoise(4, true);
    const spaceNoise = ctx.createBufferSource();
    spaceNoise.buffer = noiseBuf;
    spaceNoise.loop = true;
    const snFilter = ctx.createBiquadFilter();
    snFilter.type = "lowpass";
    snFilter.frequency.value = 150;
    const snGain = ctx.createGain();
    snGain.gain.value = 0.55;
    spaceNoise.connect(snFilter).connect(snGain).connect(spaceBus);
    spaceNoise.start();

    const trem = ctx.createOscillator();
    trem.frequency.value = 0.06;
    const tremGain = ctx.createGain();
    tremGain.gain.value = 0.14;
    trem.connect(tremGain).connect(spaceBus.gain);
    trem.start();

    // ---- sky bed: band-passed wind with a slow roaming filter
    const windBus = ctx.createGain();
    windBus.gain.value = 0;
    windBus.connect(master);
    this.windBus = windBus;
    const wind = ctx.createBufferSource();
    wind.buffer = this.makeNoise(3, false);
    wind.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 520;
    bp.Q.value = 0.7;
    const wf = ctx.createOscillator();
    wf.frequency.value = 0.05;
    const wfGain = ctx.createGain();
    wfGain.gain.value = 260;
    wf.connect(wfGain).connect(bp.frequency);
    wf.start();
    const wGain = ctx.createGain();
    wGain.gain.value = 1.15;
    wind.connect(bp).connect(wGain).connect(windBus);
    wind.start();
  }

  makeNoise(seconds, brown) {
    const ctx = this.ctx;
    const buf = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < d.length; i++) {
      const w = Math.random() * 2 - 1;
      if (brown) { last = (last + 0.02 * w) / 1.02; d[i] = last * 3.2; }
      else d[i] = w * 0.55;
    }
    return buf;
  }

  toggle() {
    this.ensure();
    if (!this.ctx) return false;
    if (this.ctx.state === "suspended") this.ctx.resume();
    this.on = !this.on;
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setTargetAtTime(this.on ? 0.42 : 0, t, 0.35);
    return this.on;
  }

  setPhase(p) {
    this.phase = p;
    if (!this.ctx) return;
    const wind = Math.min(Math.max((p - 0.42) / 0.2, 0), 1);
    const t = this.ctx.currentTime;
    this.windBus.gain.setTargetAtTime(wind, t, 0.4);
    this.spaceBus.gain.setTargetAtTime(1 - wind * 0.85, t, 0.4);
  }

  dispose() {
    if (this.ctx) { this.ctx.close(); this.ctx = null; }
  }
}
